import {PathConfigMap} from '@react-navigation/core';

import config from '../config';
import {
  AirtableBrand,
  AirtableFeedItem,
  AirtablePromotionItem,
} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {Department} from '../models/middleware';
import {
  AccountScreenAction,
  InformationContentType,
} from '../models/navigationTypes';
import {rootStore} from '../stores/rootStore';
import {OrderLine} from '../transport/apiServiceResponse';
import {codeifyName} from '../utils/string';

/*
iOS:
- xcrun simctl openurl booted https://beta.sipnsave.com.au/brands/bundaberg
- xcrun simctl openurl booted https://beta.sipnsave.com.au/promotions/footy-wins-back
- xcrun simctl openurl booted https://beta.sipnsave.com.au/promotions
iOS/Android:
- npx uri-scheme open "https://bottlemart-brand-dev.lmg-bw.au/brands/bundaberg" --ios
- npx uri-scheme open "https://bottlemart-brand-dev.lmg-bw.au/brands/bundaberg" --android
*/

const stringifyDepartment = (department: Department) =>
  codeifyName(department.name);

export const findDepartment = (
  departmentCode: string,
): Department | undefined => {
  const rangeStore = rootStore?.rangeStore;
  const department = rangeStore?.departments?.find(
    (rangeStoreDepartment: Department) =>
      codeifyName(rangeStoreDepartment.name) === departmentCode?.toLowerCase(),
  );
  return department;
};

const parseDepartmentCode = (departmentCode: string) =>
  findDepartment(departmentCode);

const parseSubDepartmentCode = (
  departmentCode: string,
): Department | undefined => {
  if (departmentCode === 'view-all') {
    return {
      id: 'view-all',
      name: 'View All',
      parent: null,
      position: 0,
      promote_at_checkout: false,
      type: 'departments',
      links: {
        icon: null,
      },
    };
  }
  return findDepartment(departmentCode);
};

type RNLinkingOptionsConfig = {
  initialRouteName?: keyof {};
  screens: PathConfigMap<{}>;
};

export const navStructure: RNLinkingOptionsConfig = {
  screens: {
    // TODO: initialRouteNames!
    [AppRoute.ROOT]: {
      screens: {
        [AppRoute.MAIN]: {
          screens: {
            // Main/base/top routes
            [AppRoute.HOME]: '',
            [AppRoute.SHOP]: 'shop',
            [AppRoute.DISCOVER]: 'discover',
            [AppRoute.CATALOGUE]: 'catalogues',
            [AppRoute.CART]: 'cart',
            ...(config.FF_LOYALTY_ENABLED
              ? {
                  [AppRoute.REWARDS]: 'rewards',

                  [AppRoute.MEMBERS_PRICE_LIST]: {
                    path: 'shop/members/:external_sell_type',
                  },

                  [AppRoute.EXCLUSIVE_OFFER_LIST]: {
                    path: 'offers',
                  },
                }
              : {}),

            // Drawer
            [AppRoute.MY_STORE]: 'my-store',
            [AppRoute.CONTACT_US]: 'contact-us',
            [AppRoute.INFORMATION]: 'information',
            [AppRoute.RESPONSIBLE_SERVICE]: 'responsible-service',
            [AppRoute.SELF_EXCLUSION]: 'self-exclusion',
            [AppRoute.WISHLIST]: 'wishlist', // not working
            [AppRoute.PROMOTIONS]: 'promotions', // not working

            [AppRoute.NEW_SEARCH]: 'search',

            // "Pages"
            // "Other"
            [AppRoute.ACCOUNT_PASSWORD_RESET]: 'password',

            // "Discover"
            [AppRoute.ARTICLE]: {
              path: 'discover/:article',
              stringify: {
                article: (article: AirtableFeedItem) => article.url || '',
              },
              parse: {
                article: (articleId: string) =>
                  rootStore?.feedStore?.discoverList?.find(
                    (feedItem: AirtableFeedItem) =>
                      feedItem.url?.toLowerCase() === articleId?.toLowerCase(),
                  ),
              },
            },

            // "Shop"
            [AppRoute.PRODUCT]: {
              path: 'shop/product/:product',
              stringify: {
                product: (product: OrderLine) => product?.product_id,
              },
            },
            [AppRoute.FAVOURITE_BRANDS]: 'shop/brands',
            [AppRoute.BRAND]: {
              path: 'shop/brands/:brand',
              stringify: {
                brand: (brand: AirtableBrand) =>
                  brand.brand ? brand.brand.replaceAll(' ', '-') : '',
              },
              parse: {
                brand: (brandName: string) =>
                  rootStore?.brandsStore?.brands?.find(
                    (brand: AirtableBrand) =>
                      brand.brand?.toLowerCase() === brandName?.toLowerCase(),
                  ),
              },
            },
            [AppRoute.DEPARTMENT]: {
              path: 'shop/categories/:department',
              stringify: {
                department: stringifyDepartment,
              },
              parse: {
                department: parseDepartmentCode,
              },
            },
            [AppRoute.SUB_DEPARTMENT]: {
              path: 'shop/categories/:department/:subDepartment',
              stringify: {
                department: stringifyDepartment,
                subDepartment: stringifyDepartment,
              },
              parse: {
                department: parseDepartmentCode,
                subDepartment: parseSubDepartmentCode,
              },
            },

            ...(config.FF_LOYALTY_ENABLED
              ? {
                  [AppRoute.EXCLUSIVE_OFFER_DETAIL]: {
                    path: 'offers/:mflId',
                  },

                  [AppRoute.ACCOUNT_EMAIL_VERIFICATION]:
                    'account/email-verification',
                }
              : {}),

            // "Promotions"
            [AppRoute.PROMOTION]: {
              path: 'promotions/:promotion',
              stringify: {
                promotion: (promotion: AirtablePromotionItem) =>
                  promotion?.url || '',
              },
              parse: {
                promotion: (promotionId: string) => {
                  if (rootStore?.promotionsStore.rawPromotions?.length) {
                    const promotion =
                      rootStore?.promotionsStore.rawPromotions.find(
                        p =>
                          p.activationUrlName?.toLowerCase() ===
                          promotionId.toLowerCase(),
                      );
                    return promotion;
                  }
                },
              },
            },
            // TODO: Should 'promotions' link to "current promotions"?
            [AppRoute.PROMOTIONS_CURRENT]: 'promotions/current',
            [AppRoute.PROMOTIONS_WINNERS]: 'promotions/winners',
            // TODO: XXX This shows to iframe link in the URL ☠️☠️☠️☠️ DANGER
            [AppRoute.PROMOTION_IFRAME]: 'promotions/go',

            // Information
            [AppRoute.INFORMATION_CONTENT]: {
              initialRouteName: AppRoute.INFORMATION_CONTENT,
              path: 'information/:target',
              stringify: {
                target: (target: InformationContentType) => target?.toString(),
              },
              parse: {
                target: (target: string) => target as InformationContentType,
              },
            },

            // Contact Us
            [AppRoute.CONTACT_US_SUBMIT_TICKET]: 'contact-us/ticket',

            // Responsible Service
            [AppRoute.RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS]:
              'responsible-service/liquor-licensing',
            [AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST]:
              'responsible-service/third-party-support',

            // Account
            // TODO: this is reachable even if the user is already logged-in
            [AppRoute.ACCOUNT_NOT_LOGGED_IN]: 'check-email',
            [AppRoute.ACCOUNT_LOGIN]: 'login',
            [AppRoute.ACCOUNT_LOGGED_IN]: 'account',
            [AppRoute.ACCOUNT_RECENT_ORDERS]: 'account/orders',
            [AppRoute.ACCOUNT_ORDER_VIEW]: 'account/order',
            [AppRoute.ACCOUNT_DELETE]: 'account/delete',
            [AppRoute.ACCOUNT_PAYMENT_METHOD]: 'account/payment',
            [AppRoute.ACCOUNT_CHANGE_PASSWORD]: {
              path: 'account/change-password',
              parse: {
                action: AccountScreenAction.CHANGE_PASSWORD,
              },
            },
            [AppRoute.ACCOUNT_CREATE]: {
              path: 'account/create',
              parse: {
                action: () => AccountScreenAction.CREATE,
              },
            },

            [AppRoute.ACCOUNT_DETAIL]: {
              path: 'account/update',
              parse: {
                action: () => AccountScreenAction.UPDATE,
              },
            },

            [AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS]: {
              path: 'account/missing-details',
              parse: {
                action: () => AccountScreenAction.COLLECT_MISSING_DETAILS,
              },
            },

            /**
             * Routes added since loyalty project
             */
            ...(config.FF_LOYALTY_ENABLED
              ? {
                  [AppRoute.ACCOUNT_PHONE_VERIFICATION]:
                    'account/phone-verification',
                  [AppRoute.ACCOUNT_SUCCESS]: 'account/success',

                  /**
                   * This is the screen in account management. When hasRewards=false is similar to the more rewards tab.
                   */
                  [AppRoute.ACCOUNT_REWARDS]: 'account/rewards',

                  [AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES]:
                    'account/communications-preferences',
                }
              : {}),

            ...(!config.FF_LOYALTY_ENABLED
              ? {
                  // Cart
                  [AppRoute.CART_ORDER_CHECKOUT]: 'cart/checkout',
                }
              : {}),

            [AppRoute.CART_ORDER_PROCESSING]: 'cart/processing',
            [AppRoute.CART_ORDER_COMPETITIONS]: 'cart/competitions',
            [AppRoute.CART_ORDER_CONFIRMATION]: 'cart/processed',
            [AppRoute.THREEDS_IFRAME_FULLSCREEN]: 'cart/3ds',

            // // Select store
            [AppRoute.MY_STORE_SUBURB_SELECTION]: 'find-suburb',
            // TODO: xxx this breaks store selection, why?
            // [AppRoute.MY_STORE_STORE_SELECTION]: 'choose-store',

            // Gift Cards
            [AppRoute.GIFT_CARDS_PROMOTION_WAITER]: 'gift-card-loading',
            [AppRoute.GIFT_CARDS_OPTIONS]: 'gift-cards-menu',
            [AppRoute.GIFT_CARDS_HOW_TO_REDEEM]: 'gift-cards-how-to-redeem',
            [AppRoute.GIFT_CARDS_CHECK_BALANCE]: 'gift-cards-balance',
            [AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS]:
              'gift-cards-terms-and-conditions',

            // Special deep links
            [AppRoute.STORE_SWITCH_LINK]: 'store/:shopSalesforceId',

            // Beer board (web-only)
            [AppRoute.BEER_BOARD]: 'store/:shopSalesforceId/beer-board',

            // Gift Cards Store
            [AppRoute.GIFT_CARDS_MAIN]: {
              path: 'gift-cards',
              screens: {
                [AppRoute.GIFT_CARDS_HOME]: '/',
                [AppRoute.GIFT_CARDS_STORE_REVIEW]: 'review',
                [AppRoute.GIFT_CARDS_STORE_CHECKOUT]: 'checkout',
                [AppRoute.GIFT_CARDS_STORE_SECRET_CART]: 'secret-cart',
                [AppRoute.GIFT_CARDS_STORE_ORDER_CONFIRMATION]:
                  'order/confirmation',
              },
            },
          },
        },
      },
    },
    [AppRoute.ROOT_STORE_SELECTION_FLOW]: {
      screens: {
        [AppRoute.MY_STORE_SUBURB_SELECTION]: 'welcome/find-suburb',
        [AppRoute.MY_STORE_STORE_SELECTION]: 'welcome/choose-store',
        // These screens are here because we needed a quick solution to show
        // the Gift Card Promotion Details screen without the need to select a store.
        [AppRoute.ROOT_GIFT_CARDS_PROMOTION]: 'gift-card-promotion',
        [AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS]:
          'gift-card-terms-and-conditions',
      },
    },
    ...(config.FF_LOYALTY_ENABLED
      ? {
          /**
           * Checkout flow is separated now
           */
          [AppRoute.ROOT_CHECKOUT_FLOW]: {
            screens: {
              // Cart
              [AppRoute.CART_ORDER_CHECKOUT]: 'cart/checkout',
              [AppRoute.CART_ORDER_CHECKOUT_REVIEW]: 'cart/checkout-review',
              [AppRoute.CART_ORDER_CHECKOUT_PAYMENT]: 'cart/checkout-payment',
            },
          },
          [AppRoute.ROOT_REWARDS_FLOW]: {
            screens: {
              [AppRoute.REWARDS_SIGN_IN]: 'rewards/sign-in',
              [AppRoute.REWARDS_WELCOME_BACK]: 'rewards/welcome-back',
              [AppRoute.REWARDS_SIGN_UP]: 'rewards/sign-up',
              [AppRoute.REWARDS_VERIFICATION]: 'rewards/verification',
              [AppRoute.REWARDS_SUCCESS]: 'rewards/success',
              [AppRoute.REWARDS_TERMS_AND_CONDITIONS]:
                'rewards/terms-and-conditions',
              /**
               * When user clicks email link to complete full sign up
               */
              [AppRoute.REWARDS_FULL_SIGN_UP]: 'rewards/full-sign-up',
              [AppRoute.REWARDS_FULL_SUCCESS]: 'rewards/full-success',

              /**
               * Routes for when User has clicked Join More Rewards
               */
              [AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS]:
                'rewards/activate-terms-and-conditions',
              [AppRoute.REWARDS_ACTIVATE_VERIFICATION]:
                'rewards/activate-verification',
              [AppRoute.REWARDS_ACTIVATE_SUCCESS]: 'rewards/activate-success',
            },
          },
        }
      : {}),
    ...(config.KITCHENSINK_ENABLED
      ? {
          [AppRoute.ROOT_KITCHENSINK]: {
            screens: {
              [AppRoute.KITCHENSINK_INDEX]: 'kitchensink',
              [AppRoute.KITCHENSINK_CARDS]: 'kitchensink/cards',
            },
          },
        }
      : {}),
    [AppRoute.ROOT_APP_BUILD_INFO]: 'build-info',
  },
};
